import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import Icons from "@/assets";

interface Props {
  open: boolean;
  onClose: () => void;
}

const Section = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ mb: 3 }}>{children}</Box>
);

const Heading = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#191919", mb: 1 }}>
    {children}
  </Typography>
);

const SubHeading = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#191919", mb: 0.75 }}>
    {children}
  </Typography>
);

const Paragraph = ({ children, color }: { children: React.ReactNode; color?: string }) => (
  <Typography sx={{ fontSize: 13, color: color ?? "#444", mb: 1, lineHeight: 1.6 }}>
    {children}
  </Typography>
);

const Bullet = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontSize: 13, color: "#444", mb: 0.5, lineHeight: 1.6 }}>
    {children}
  </Typography>
);

const PrivacyPolicyModal: React.FC<Props> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#191919" }}>
          Privacy Policy
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: "#FFFFFF" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
          <img src={Icons.Logo} alt="eSwayam" style={{ height: 48 }} />
        </Box>

        <Section>
          <Typography sx={{ fontSize: 12, color: "#888", mb: 1.5 }}>
            Last Updated: February 6, 2026
          </Typography>
          <Paragraph>
            eSwayam ("we", "our", or "us") respects your privacy and is committed to protecting it. This Privacy Policy explains how we collect, use, and handle your information when you use the eSwayam mobile application.
          </Paragraph>
          <Paragraph>By using the App, you agree to the practices described in this Privacy Policy.</Paragraph>
          <Paragraph>
            This Privacy Policy is published in compliance with the Digital Personal Data Protection Act, 2023 and other applicable laws in India, to the extent in force from time to time.
          </Paragraph>
        </Section>

        <Section>
          <Heading>1. Information We Collect</Heading>
          <SubHeading>1.1 Information You Provide</SubHeading>
          <Paragraph>We collect only the minimum information necessary to provide our services:</Paragraph>
          <Bullet>• Username (required for account creation and identification)</Bullet>
          <Paragraph>We do not collect:</Paragraph>
          <Bullet>• Phone number</Bullet>
          <Bullet>• Date of birth</Bullet>
          <Bullet>• Gender</Bullet>
          <Bullet>• Address</Bullet>
          <Bullet>• Government-issued identification</Bullet>

          <SubHeading>1.2 Media Content</SubHeading>
          <Paragraph>The App allows you to:</Paragraph>
          <Bullet>• Capture photos or videos using your device camera</Bullet>
          <Bullet>• Select photos from your device gallery</Bullet>
          <Paragraph>These photos or videos may be uploaded to our servers as part of the App's functionality.</Paragraph>

          <SubHeading>1.3 Location Information</SubHeading>
          <Paragraph>We collect precise location data (GPS):</Paragraph>
          <Bullet>• Only while the app is in use</Bullet>
          <Bullet>• Solely for map-related features</Bullet>
          <Paragraph>We do not collect location data in the background.</Paragraph>

          <SubHeading>1.4 Push Notifications</SubHeading>
          <Paragraph>We send transactional notifications only, such as:</Paragraph>
          <Bullet>• Account-related updates</Bullet>
          <Bullet>• Important app information</Bullet>
          <Paragraph>You can disable notifications anytime through your device settings.</Paragraph>
        </Section>

        <Section>
          <Heading>2. Information We Do Not Collect</Heading>
          <Paragraph>We do not collect:</Paragraph>
          <Bullet>• Device identifiers</Bullet>
          <Bullet>• IP address</Bullet>
          <Bullet>• Advertising IDs</Bullet>
          <Bullet>• Analytics or tracking data</Bullet>
          <Bullet>• Crash or performance logs</Bullet>
          <Bullet>• Payment or financial information</Bullet>
          <Bullet>• However, basic technical information such as IP address and device type may be temporarily processed by our hosting or network infrastructure for connectivity, security and fraud‑prevention purposes</Bullet>
        </Section>

        <Section>
          <Heading>3. How We Use Information</Heading>
          <Paragraph>We use the collected information only to:</Paragraph>
          <Bullet>• Provide and operate the App</Bullet>
          <Bullet>• Enable map-based functionality</Bullet>
          <Bullet>• Allow media capture and uploads</Bullet>
          <Bullet>• Maintain account functionality</Bullet>
          <Bullet>• Send essential notifications</Bullet>
          <Paragraph>We do not use your data for advertising or marketing purposes.</Paragraph>
        </Section>

        <Section>
          <Heading>4. User Accounts</Heading>
          <Bullet>• An account is required to use the App</Bullet>
          <Bullet>• Users can request account deletion through the in-app option or through the website form</Bullet>
          <Bullet>• Once deleted, your account will no longer be accessible</Bullet>
        </Section>

        <Section>
          <Heading>5. User Rights and Controls</Heading>
          <Paragraph>You can:</Paragraph>
          <Bullet>• View your username within the App</Bullet>
          <Bullet>• Delete your account at any time</Bullet>
          <Paragraph>You currently cannot edit your username after account creation.</Paragraph>
        </Section>

        <Section>
          <Heading>6. Third-Party Services</Heading>
          <Paragraph>We use limited third-party services only where necessary:</Paragraph>
          <Bullet>• Firebase Cloud Messaging (FCM) – for push notifications</Bullet>
          <Bullet>• Google Places API – used via our backend for map-related features</Bullet>
          <Paragraph>We do not use advertising SDKs or third-party analytics tools.</Paragraph>
        </Section>

        <Section>
          <Heading>7. Children's Privacy</Heading>
          <Paragraph>eSwayam is not intended for children under the age of 13.</Paragraph>
          <Paragraph>We do not knowingly collect personal data from children under 13.</Paragraph>
          <Paragraph>If you believe a child has provided us with personal data, please contact us and we will take appropriate action.</Paragraph>
        </Section>

        <Section>
          <Heading>8. International Use</Heading>
          <Paragraph>
            eSwayam is operated globally. By using the App, you understand that your information may be processed in countries outside your own, in accordance with this Privacy Policy.
          </Paragraph>
        </Section>

        <Section>
          <Heading>9. Changes to This Privacy Policy</Heading>
          <Paragraph>We may update this Privacy Policy from time to time.</Paragraph>
          <Paragraph>Any changes will be posted on this page, and the updated date will be revised above.</Paragraph>
          <Paragraph>Continued use of the App after changes means you accept the updated policy.</Paragraph>
        </Section>

        <Section>
          <Heading>10. Data Sharing</Heading>
          <SubHeading>10.1 Service Providers</SubHeading>
          <Paragraph>We may share limited data with trusted service providers solely to operate and support the App, including:</Paragraph>
          <Bullet>• Firebase Cloud Messaging (FCM) – used only to deliver transactional push notifications</Bullet>
          <Bullet>• Google Places API – accessed via our backend for map-related functionality. No personal user details are shared with Google Places</Bullet>
          <Paragraph>All service providers are contractually required to use data only for the intended purpose and to protect it appropriately.</Paragraph>

          <SubHeading>10.2 Legal Obligations</SubHeading>
          <Paragraph>
            We may disclose user information if required to do so by law, regulation, legal process, or valid governmental request, or to protect the rights, safety, and security of our users or the App.
          </Paragraph>

          <SubHeading>10.3 Business Transfers</SubHeading>
          <Paragraph>
            In the event of a merger, acquisition, restructuring, or sale of assets, user data may be transferred as part of the business transaction. In such cases, we will ensure that your data continues to be protected under terms consistent with this Privacy Policy.
          </Paragraph>
        </Section>

        <Section>
          <Heading>11. Data Storage and Security</Heading>
          <SubHeading>11.1 Data Storage</SubHeading>
          <Paragraph>User data is stored on our own servers, located in:</Paragraph>
          <Bullet>• Central India</Bullet>
          <Bullet>• East Asia</Bullet>
          <Paragraph>Data may be processed across these regions to ensure reliable operation of the App.</Paragraph>

          <SubHeading>11.2 Security Measures</SubHeading>
          <Paragraph>We take reasonable and appropriate measures to protect user data, including:</Paragraph>
          <Bullet>• Secure communication using HTTPS REST APIs</Bullet>
          <Bullet>• JWT-based authentication for access control</Bullet>
          <Bullet>• Restricted access to data, limited to authorized personnel only</Bullet>
          <Paragraph>
            While no system can guarantee absolute security, we continuously work to safeguard your information against unauthorized access, alteration, or misuse.
          </Paragraph>

          <SubHeading>11.3 Data Retention and Deletion</SubHeading>
          <Paragraph>When a user requests account deletion:</Paragraph>
          <Bullet>• The account is immediately deactivated and becomes inaccessible</Bullet>
          <Bullet>• Personal data and associated media are scheduled for permanent deletion within 30 days, unless retention is required by law</Bullet>
          <Bullet>• Uploaded photos and videos are retained only for operational or moderation purposes during this retention period and are deleted thereafter</Bullet>
        </Section>

        <Section>
          <Heading>12. Contact Us</Heading>
          <Paragraph>If you have questions, concerns, or requests regarding this Privacy Policy or your data, contact us at:</Paragraph>
          <Typography sx={{ fontSize: 13 }}>
            <a
              href="mailto:info@eswayam.org"
              style={{ color: "#7747FB", textDecoration: "none" }}
            >
              info@eswayam.org
            </a>
          </Typography>
        </Section>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyPolicyModal;
